/**
 * useMapRefetchOnNavigation
 * 
 * Custom hook to handle map data refetching on various navigation events
 * Refetches when:
 * - Page visibility changes (tab becomes visible)
 * - Window gains focus
 * - Navigation key changes (back/forward)
 * 
 * @param {Object} params - Hook parameters
 * @param {Object} params.userLocation - Current user location
 * @param {Function} params.fetchCallback - Callback to refetch data (e.g., fetchNearbyEntities)
 * @param {Object} params.location - React Router location object
 * @param {Object} params.hasInitialized - Ref to track initialization
 * @param {Object} params.previousLocationKey - Ref to track previous location key
 * @param {boolean} params.forceFresh - Whether to force fresh fetch (bypass cache)
 * 
 * @returns {void} - Side effects only
 */

import { useEffect } from 'react'

export function useMapRefetchOnNavigation({
  userLocation,
  fetchCallback,
  location,
  hasInitialized,
  previousLocationKey,
  forceFresh = true
}) {
  // Refetch entities when user navigates back to page (e.g., after confirming sighting)
  // This ensures markers show updated confirmation colors
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Only refetch if page becomes visible and we have a location
      if (!document.hidden && userLocation && fetchCallback) {
        // Force fresh fetch to get updated confirmation counts
        fetchCallback(userLocation, null, forceFresh)
      }
    }

    const handleFocus = () => {
      // Refetch when window gains focus (handles back navigation within SPA)
      if (userLocation && fetchCallback) {
        fetchCallback(userLocation, null, forceFresh)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
    }
  }, [userLocation, fetchCallback, forceFresh])

  // Refetch when navigating back from another page
  useEffect(() => {
    // Skip if not initialized or no location
    if (!hasInitialized?.current || !userLocation || !location) {
      return
    }
    
    // Check if location.key has changed (indicates navigation happened)
    const keyChanged = previousLocationKey?.current !== null && 
                      previousLocationKey?.current !== location.key
    
    if (keyChanged && fetchCallback) {
      fetchCallback(userLocation, null, forceFresh)
    }
    
    // Update previous key for next comparison
    if (previousLocationKey) {
      previousLocationKey.current = location.key
    }
  }, [location?.key, userLocation, fetchCallback, hasInitialized, previousLocationKey, forceFresh])
}
